import { makeObservable, observable, runInAction, computed } from 'mobx';
import { isEqual } from 'lodash';
import type { ContractCause } from '../../history/ContractDraftHistory';
import { ContractsIngestionRuntimeInputDto } from '../../types';
import { InputBaseModel, NodeBaseModel } from '..';
import { TypeUtils } from '../../../common/misc';
import { SourceModelBase, StaticSourceModel } from '../../../custom_shared/models';
import { SourceProvider } from '../../../custom_shared/misc';
import { InputIds, InstanceUtils } from '../../misc';

interface FetchSourceOptionsParams {
    ignoreCache?: boolean;
}

export default class InputModelWithSource extends InputBaseModel {
    sourceProvider: SourceProvider;
    useCachedSourceOptions: boolean = false;

    private requestId = 0;
    private hierarchyId = 0;
    private sourceModel?: SourceModelBase;
    private pendingRefresh?: {
        isCurrent: () => boolean;
        restart: (input: InputModelWithSource) => void;
    };
    private readonly minimumLoadingTime = 500;
    private readonly debounceTime = 300;

    constructor(node: NodeBaseModel, data: ContractsIngestionRuntimeInputDto, source?: SourceModelBase) {
        super(node, data);

        this.createSourceProvider(source);
        makeObservable(this, {
            sourceProvider: observable,
            useCachedSourceOptions: observable,
            isLoading: computed,
            isParentLoading: computed
        });
    }

    fetchSourceOptionsHierarchy(cause = this.ownerNode.history.causeFor(this)): void {
        const hierarchy = ++this.hierarchyId;
        const scheduledValueVersion = this.valueVersion;
        const requests = this.ownerNode.sourceRequests;
        const generation = requests.generation;
        const refresh = {
            isCurrent: () =>
                hierarchy === this.hierarchyId &&
                scheduledValueVersion === this.valueVersion &&
                requests.generation === generation,
            restart: (input: InputModelWithSource) => input.fetchSourceOptionsHierarchy(cause)
        };
        this.pendingRefresh = refresh;
        if (this.sourceProvider.isLinkedSource) {
            this.sourceProvider.setLoading(true);
        }
        const task = new Promise<void>(resolve => setTimeout(resolve, this.debounceTime)).then(async () => {
            if (hierarchy !== this.hierarchyId || requests.generation !== generation) {
                return;
            }
            if (scheduledValueVersion !== this.valueVersion) {
                if (this.pendingRefresh === refresh) {
                    this.pendingRefresh = undefined;
                }
                this.sourceProvider.setLoading(false);
                return;
            }
            await this.fetchSourceOptionsWithChildren({}, cause);
        });
        this.ownerNode.sourceRequests.track(task);
    }

    // Reconstruction transfers only live work. A later manual edit has already
    // invalidated its old lookup and must not be pruned by a restarted request.
    suspendSourceRefresh() {
        const restart = this.pendingRefresh?.isCurrent() ? this.pendingRefresh.restart : undefined;
        this.pendingRefresh = undefined;
        ++this.requestId;
        ++this.hierarchyId;
        this.sourceProvider.setLoading(false);
        return restart;
    }

    get isLoading(): boolean {
        return this.sourceProvider.loading || this.isParentLoading;
    }

    get isParentLoading(): boolean {
        return this.parentInputs.filter(InstanceUtils.isInputWithSource).some(parentInput => parentInput.isLoading);
    }

    get formattedValue(): unknown {
        if (TypeUtils.isStringArray(this.value)) {
            return this.value.map(value => this.sourceProvider.options.find(o => o.value === value)?.label ?? value);
        }

        return this.sourceProvider.options.find(o => o.value === this.value)?.label ?? this.value;
    }

    fetchSourceOptions(
        params: FetchSourceOptionsParams = {},
        cause = this.ownerNode.history.causeFor(this)
    ): Promise<boolean> {
        const task = this.loadSourceOptions(params, cause);
        return this.ownerNode.sourceRequests.track(task);
    }

    fetchSourceOptionsWithChildren(
        params: FetchSourceOptionsParams = {},
        cause = this.ownerNode.history.causeFor(this)
    ): Promise<void> {
        const task = this.loadSourceHierarchy(params, cause);
        return this.ownerNode.sourceRequests.track(task);
    }

    private async loadSourceOptions(
        { ignoreCache }: FetchSourceOptionsParams,
        cause?: ContractCause,
        withChildren = false
    ): Promise<boolean> {
        const request = ++this.requestId;
        let valueVersion = this.valueVersion;
        const parents = this.parentInputValuesById;
        const history = this.ownerNode.history;
        const requests = this.ownerNode.sourceRequests;
        const generation = requests.generation;
        // Isolate linked-provider writes until this request is still current. Shared
        // sources keep their existing cache/lookup rules.
        const provider = this.sourceModel ? new SourceProvider(this.sourceModel) : this.sourceProvider;
        const startedAt = Date.now();
        const current = () =>
            request === this.requestId &&
            valueVersion === this.valueVersion &&
            isEqual(parents, this.parentInputValuesById) &&
            requests.generation === generation &&
            this.ownerNode.inputs.includes(this);
        const refresh = {
            isCurrent: current,
            restart: (input: InputModelWithSource) => {
                if (withChildren) {
                    void input.fetchSourceOptionsWithChildren({ ignoreCache }, cause);
                } else {
                    void input.fetchSourceOptions({ ignoreCache }, cause);
                }
            }
        };
        this.pendingRefresh = refresh;
        try {
            if (this.sourceProvider.isLinkedSource) {
                this.sourceProvider.setLoading(true);
            }
            if (this.parentInputs.length > 0) {
                await provider.getSourceOptionsByParent(Object.values(parents).flat(), parents);
            } else {
                await provider.getSourceOptions(ignoreCache);
            }
            if (!current()) {
                return false;
            }
            if (provider !== this.sourceProvider) {
                this.sourceProvider.setSourceData(provider.sourceData);
            }
            history.observe(this, cause);
            history.withCause(cause, () => this.syncValueWithOptions());
            if (this.sourceProvider.isLinkedSource) {
                runInAction(() => {
                    this.useCachedSourceOptions = true;
                });
            }
            valueVersion = this.valueVersion;
        } catch (e) {
            console.error(`Error while fetching source options for input '${this.id} ${this.guid}'.`, e);
            // A failed lookup retains its values. Existing descendants still refresh
            // against those retained parents; only stale work is discarded.
        } finally {
            if (this.sourceProvider.isLinkedSource) {
                await new Promise(resolve =>
                    setTimeout(resolve, Math.max(0, this.minimumLoadingTime - (Date.now() - startedAt)))
                );
                if (request === this.requestId) {
                    this.sourceProvider.setLoading(false);
                }
            }
            if (this.pendingRefresh === refresh) {
                this.pendingRefresh = undefined;
            }
        }
        return current();
    }

    private async loadSourceHierarchy(params: FetchSourceOptionsParams, cause?: ContractCause): Promise<void> {
        const requests = this.ownerNode.sourceRequests;
        const generation = requests.generation;
        if (!(await this.loadSourceOptions(params, cause, true)) || requests.generation !== generation) {
            return;
        }
        const childCause = cause
            ? { rootId: cause.rootId, trigger: this.ownerNode.history.reference(this) }
            : undefined;
        await Promise.all(
            this.childInputs
                .filter(InstanceUtils.isInputWithSource)
                .map(input => input.fetchSourceOptionsWithChildren(params, childCause))
        );
    }

    private get parentInputValuesById(): Record<string, string[]> {
        return this.parentInputs.reduce(
            (valuesById, parentInput) => {
                valuesById[parentInput.id] = this.toStringArray(parentInput.value);
                return valuesById;
            },
            {} as Record<string, string[]>
        );
    }

    private toStringArray(value: unknown): string[] {
        if (TypeUtils.isString(value)) {
            return value.length > 0 ? [value] : [];
        }

        if (TypeUtils.isStringArray(value)) {
            return value;
        }

        return [];
    }

    private syncValueWithOptions(): void {
        if (TypeUtils.isStringArray(this.value)) {
            return this.setValue(this.value.filter(v => this.sourceProvider.values.includes(v)));
        }

        if (TypeUtils.isString(this.value) && !this.sourceProvider.values.includes(this.value)) {
            this.setValue(null);
        }

        if (
            this.id === InputIds.OtherConditions.ReasonForClause &&
            this.required &&
            !this.isReadOnly &&
            !this.hasValue &&
            this.sourceProvider.values.length === 1
        ) {
            this.setValue(this.sourceProvider.values[0]);
        }
    }

    private createSourceProvider(source?: SourceModelBase) {
        this.sourceModel = source;
        runInAction(() => {
            if (source) {
                this.sourceProvider = new SourceProvider(source);
                return;
            }

            console.warn(`Source not found for input '${this.id} ${this.guid}'`);
            this.sourceProvider = new SourceProvider(new StaticSourceModel({ id: '', type: '', link: null, data: [] }));
        });
    }
}
